import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Sparkles, ArrowRight, Calendar, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/App";

// Google icon component
const GoogleIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
  </svg>
);

export default function AuthPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);

  const handleGoogleSignIn = () => {
    setLoading(true);
    // Redirect to real backend OAuth route
    const backendUrl = process.env.REACT_APP_BACKEND_URL || "http://localhost:8000";
    window.location.href = `${backendUrl}/auth/google`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-indigo-950 to-slate-950 overflow-x-hidden">
      {/* Cosmic background effects */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-20 left-10 w-72 h-72 bg-purple-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-pink-500/10 rounded-full blur-3xl" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-indigo-500/5 rounded-full blur-3xl" />
      </div>

      {/* Header */}
      <header className="relative z-10 p-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
            <Sparkles className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-white font-bold text-lg">HI AI</h1>
            <p className="text-white/40 text-[10px]">APP.COM</p>
          </div>
        </div>
        <a
          href="https://calendly.com/doersworld-contact"
          target="_blank"
          rel="noopener noreferrer"
          className="group flex items-center gap-2 bg-gradient-to-r from-gray-900 via-black to-gray-900 text-white px-5 py-2.5 rounded-full text-sm font-bold shadow-lg shadow-black/30 hover:shadow-xl hover:shadow-purple-500/20 hover:scale-105 transition-all duration-300 border-2 border-gray-300"
        >
          <Calendar className="w-4 h-4 group-hover:animate-pulse" />
          Book a Call
          <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
        </a>
      </header>

      {/* Main Content */}
      <main className="relative z-10 flex flex-col items-center justify-center min-h-[calc(100vh-80px)] px-4">
        <div className="w-full max-w-md">
          {/* Logo & Branding */}
          <motion.div
            className="text-center mb-10"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <motion.div
              className="w-24 h-24 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-purple-600 via-pink-500 to-orange-500 flex items-center justify-center shadow-2xl shadow-purple-500/30"
              animate={{
                boxShadow: [
                  "0 25px 50px -12px rgba(168, 85, 247, 0.3)",
                  "0 25px 50px -12px rgba(236, 72, 153, 0.3)",
                  "0 25px 50px -12px rgba(168, 85, 247, 0.3)"
                ]
              }}
              transition={{ duration: 3, repeat: Infinity }}
            >
              <Sparkles className="w-12 h-12 text-white" />
            </motion.div>

            <h1 className="text-4xl md:text-5xl font-bold text-white mb-2">
              Welcome to <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 via-pink-400 to-orange-400">Doers World</span>
            </h1>
            <p className="text-white/60 text-sm">
              An AGENTIC AI for Dreamers • Doers • Disrupters • Deep Humans
            </p>
          </motion.div>

          {/* Auth Card */}
          <motion.div
            className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-3xl p-8 shadow-2xl"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="text-center mb-8">
              <h2 className="text-xl font-bold text-white mb-2">Get Started</h2>
              <p className="text-white/50 text-sm">Sign in to discover your potential</p>
            </div>

            {/* Google Sign In Button */}
            <Button
              className="w-full h-14 text-lg font-semibold bg-white hover:bg-gray-100 text-gray-800 rounded-xl flex items-center justify-center gap-3 transition-all duration-300 hover:scale-[1.02] shadow-lg"
              onClick={handleGoogleSignIn}
              disabled={loading}
              data-testid="google-signin-btn"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-gray-400 border-t-gray-800 rounded-full animate-spin" />
              ) : (
                <GoogleIcon />
              )}
              {loading ? "Signing in..." : "Continue with Google"}
            </Button>

            {/* Divider */}
            <div className="flex items-center gap-3 my-6">
              <div className="flex-1 h-px bg-white/10" />
              <span className="text-white/40 text-xs">SECURE SIGN IN</span>
              <div className="flex-1 h-px bg-white/10" />
            </div>

            {/* Trust indicators */}
            <div className="grid grid-cols-3 gap-2 text-center">
              {[
                { icon: "🔒", label: "Secure" },
                { icon: "⚡", label: "Fast" },
                { icon: "🌍", label: "Global" }
              ].map((item, i) => (
                <div key={i} className="bg-white/5 rounded-xl p-3">
                  <span className="text-lg">{item.icon}</span>
                  <p className="text-white/40 text-[10px] mt-1">{item.label}</p>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Footer text */}
          <motion.p
            className="text-center text-white/30 text-xs mt-6"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
          >
            By signing in, you agree to our Terms of Service & Privacy Policy
          </motion.p>
        </div>
      </main>

      {/* Bottom tagline */}
      <div className="fixed bottom-4 left-0 right-0 text-center z-10">
        <p className="text-white/20 text-[10px]">
          HI AI-APP.COM • Way of Work • Soonicorn 2026
        </p>
      </div>
    </div>
  );
}
